import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth-context";
import { auth as authApi } from "@/lib/api";

const CATEGORIES = [
  { id: "beauty", label: "Skincare & Beauty", emoji: "✨" },
  { id: "fashion", label: "Fashion", emoji: "👗" },
  { id: "food", label: "Makanan & Kopi", emoji: "☕" },
  { id: "pet_care", label: "Pet Care", emoji: "🐾" },
  { id: "baby_care", label: "Baby Care", emoji: "🍼" },
  { id: "herbal", label: "Herbal / FMCG", emoji: "🌿" },
  { id: "household", label: "Household", emoji: "🏠" },
  { id: "other", label: "Lainnya", emoji: "🎁" },
];

const CHANNELS = [
  { id: "shopee", label: "Shopee" },
  { id: "tiktok", label: "TikTok Shop" },
  { id: "tokopedia", label: "Tokopedia" },
  { id: "lazada", label: "Lazada" },
  { id: "instagram", label: "Instagram" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "offline", label: "Toko Offline" },
];

export default function Onboarding() {
  const { merchant, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState(merchant?.category || "");
  const [channels, setChannels] = useState(merchant?.sales_channel || []);
  const [saving, setSaving] = useState(false);

  const toggleChannel = (id) => {
    setChannels((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  };

  const finish = async () => {
    setSaving(true);
    try {
      await authApi.updateOnboarding({ category, sales_channel: channels, onboarded: true });
      await refresh();
      toast.success("Selamat datang di Custora!");
      navigate("/dashboard");
    } catch {
      toast.error("Gagal menyimpan. Coba lagi.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="h-16 bg-white border-b border-gray-200 flex items-center px-6">
        <Logo size="sm" />
        <div className="ml-auto text-sm text-gray-500">Langkah {step} dari 2</div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-emerald-600">
            <Sparkles size={14} /> Onboarding
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-gray-900">
            Halo {merchant?.owner_name?.split(" ")[0]}, mari setup toko Anda.
          </h1>
          <p className="mt-2 text-gray-600">Kami akan pakai info ini untuk kasih rekomendasi campaign, benchmark kategori, dan repeat order engine.</p>
        </div>

        {step === 1 && (
          <Card className="p-6 sm:p-8 border-gray-200">
            <h2 className="font-heading text-xl font-semibold text-gray-900">Pilih kategori produk utama</h2>
            <p className="mt-1 text-sm text-gray-500">Ini akan menentukan siklus repeat order & benchmark yang kami tampilkan.</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`category-${c.id}`}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    category === c.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="text-2xl">{c.emoji}</div>
                  <div className="mt-2 font-semibold text-sm text-gray-900">{c.label}</div>
                  {category === c.id && (
                    <div className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold">
                      <Check size={12} strokeWidth={3} /> Dipilih
                    </div>
                  )}
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setStep(2)}
                disabled={!category}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6 font-semibold"
                data-testid="onboarding-next-1"
              >
                Lanjut <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="p-6 sm:p-8 border-gray-200">
            <h2 className="font-heading text-xl font-semibold text-gray-900">Di mana Anda berjualan?</h2>
            <p className="mt-1 text-sm text-gray-500">Pilih semua channel yang Anda gunakan (bisa lebih dari satu).</p>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CHANNELS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => toggleChannel(c.id)}
                  data-testid={`channel-${c.id}`}
                  className={`p-4 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                    channels.includes(c.id)
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    channels.includes(c.id) ? "border-emerald-600 bg-emerald-600" : "border-gray-300"
                  }`}>
                    {channels.includes(c.id) && <Check size={12} className="text-white" strokeWidth={3} />}
                  </div>
                  <span className="font-medium text-sm text-gray-900">{c.label}</span>
                </button>
              ))}
            </div>
            <div className="mt-8 flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} className="h-11" data-testid="onboarding-back">
                Kembali
              </Button>
              <Button
                onClick={finish}
                disabled={channels.length === 0 || saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6 font-semibold"
                data-testid="onboarding-finish"
              >
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Selesai & Masuk Dashboard
              </Button>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
